import { useCallback, useEffect, useState } from "react";
import { apiGet } from "../api/client";

/** A response belongs to its URL. Filter changes abort and hide earlier scopes. */
export function usePollingResource<T>(path: string | null, intervalMs = 10_000) {
  const [value, setValue] = useState<{ path: string; data: T; receivedAt: string } | null>(null);
  const [failure, setFailure] = useState<{ path: string; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision(v => v + 1), []);
  useEffect(() => {
    if (!path) { setLoading(false); return; }
    let active = true;
    let pending = false;
    let controller: AbortController | null = null;
    const read = async () => {
      if (!active || pending || document.visibilityState === "hidden") return;
      pending = true;
      controller = new AbortController();
      setLoading(true);
      try {
        const data = await apiGet<T>(path, controller.signal);
        if (active) {
          setValue({ path, data, receivedAt: new Date().toISOString() });
          setFailure(null);
        }
      } catch (error) {
        if (active && !controller.signal.aborted) {
          setFailure({ path, message: error instanceof Error ? error.message : "조회 실패" });
        }
      } finally {
        pending = false;
        if (active) setLoading(false);
      }
    };
    void read();
    const timer = window.setInterval(read, intervalMs);
    const visible = () => { if (document.visibilityState === "visible") void read(); };
    document.addEventListener("visibilitychange", visible);
    return () => {
      active = false;
      controller?.abort();
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [path, intervalMs, revision]);
  return {
    data: value?.path === path ? value.data : null,
    receivedAt: value?.path === path ? value.receivedAt : null,
    error: failure?.path === path ? failure.message : null,
    loading, refresh,
  };
}
